// INVENTORY'S READ (UI rebuild batch 5): the response shape and the one fetch, copied from screens/Inventory.tsx (which
// keeps them local because lib/api.ts has no `api.inventory` yet). The fetch goes through lib/api.ts's `request` — the
// one client, with its sign-in, its ApiError and the bound Screens landed (26 Oct 2026) — exactly as the old screen
// does. When `api.inventory` lands, both screens drop their copies and call it.
import { request, type PnlPeriodKey } from '../../../lib/api';

export type BasisSource = 'measured' | 'operator' | 'peg' | 'provisional' | 'unset';

export interface InventoryBasis {
  source: BasisSource;
  label: string;
  assumed: boolean;
  caveat: string | null;
  rate: number | null;
  opening_quantity: number | null;
  set_at: string | null;
  note: string | null;
}

export interface InventoryBook {
  key: string;
  asset: string;
  quote_currency: string;
  label: string;
  quantity: number | null;
  avg_buy_cost: number | null;
  avg_buy_cost_lifetime: number | null;
  avg_sell_price: number | null;
  avg_sell_price_period: number | null;
  book_value: number | null;
  realised_margin: number;
  realised_margin_lifetime: number;
  bought_quantity_lifetime: number;
  sold_quantity_lifetime: number;
  trades_in_period: number;
  last_trade_rate: number | null;
  last_trade_at: string | null;
  /** Emptied into its folded twin by the 2 Sept rekey and kept for history. Absent reads as live — the safe direction. */
  retired?: boolean;
  basis: InventoryBasis;
}

export interface InventoryUnbased {
  asset: string;
  label: string;
  quantity: number;
  held: number;
  on_books: number;
}

export interface InventoryNote { code: string; tone: 'warn' | 'info'; message: string }

export interface InventoryResponse {
  entity: string;
  period: { key: PnlPeriodKey; label: string; from: string | null; to: string; timezone: string };
  books: InventoryBook[];
  unbased: InventoryUnbased[];
  notes: InventoryNote[];
  meta: { minor_units: boolean; books_table: boolean; ledger_read: boolean; deals_rows: number };
}

/** GET /api/summary/inventory through the one client. */
export async function fetchInventory(period: PnlPeriodKey, days: { from?: string; to?: string }): Promise<InventoryResponse> {
  const qs = new URLSearchParams({ period });
  if (days.from) qs.set('from', days.from);
  if (days.to)   qs.set('to', days.to);
  return request<InventoryResponse>(`/api/summary/inventory?${qs}`);
}
